import React, { createContext, useContext, useState, useCallback, ReactNode, useRef, useEffect } from 'react';
import ConfirmationDialog from '../components/ConfirmationDialog/ConfirmationDialog';
import GlobalLoadingOverlay from '../components/GlobalLoadingOverlay/GlobalLoadingOverlay';
import AlertDialog from '../components/AlertDialog/AlertDialog';
import ErrorReportDialog from '../components/ErrorReportDialog/ErrorReportDialog';
import { AppErrorDetails, buildErrorReport, registerErrorReporter } from '../utils/errorReport';

export type { AppErrorDetails };

/** false = cancelled; 'confirm' = primary; 'secondary' = optional second confirm */
export type ConfirmationResult = false | 'confirm' | 'secondary';

export interface ConfirmationMessage {
  id: string;
  title: string;
  message: string;
  confirmText?: string;
  secondaryConfirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onSecondaryConfirm?: () => void;
  onCancel: () => void;
}

interface WarningAlertState {
  title: string;
  message: string;
}

interface ErrorAlertState {
  summary: string;
  reportText: string;
}

interface ToastContextType {
  showToast: (message: string, type: 'success' | 'error' | 'warning' | 'info', duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (summary: string, details?: AppErrorDetails) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  setLoading: (active: boolean, source?: string) => void;
  showConfirmation: (
    config: Omit<ConfirmationMessage, 'id' | 'onConfirm' | 'onSecondaryConfirm' | 'onCancel'>
  ) => Promise<ConfirmationResult>;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

const GlobalErrorHandlers: React.FC = () => {
  const { showError } = useToast();
  const lastReportedErrorRef = useRef<string>('');

  useEffect(() => {
    const handleWindowError = (event: ErrorEvent) => {
      const source = event.filename || '';
      if (
        source.includes('webpack') ||
        source.includes('hot-update') ||
        source.includes('react refresh')
      ) {
        return;
      }

      const message = event.error?.message || event.message || 'Unknown error';
      if (lastReportedErrorRef.current === message) {
        return;
      }
      lastReportedErrorRef.current = message;

      event.preventDefault();
      showError('אירעה שגיאה לא צפויה באפליקציה', {
        action: 'Unhandled runtime error',
        error: event.error ?? event.message,
        context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message =
        event.reason instanceof Error
          ? event.reason.message
          : String(event.reason ?? 'Unknown rejection');
      if (lastReportedErrorRef.current === message) {
        return;
      }
      lastReportedErrorRef.current = message;

      event.preventDefault();
      showError('אירעה שגיאה לא צפויה באפליקציה', {
        action: 'Unhandled promise rejection',
        error: event.reason,
      });
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [showError]);

  return null;
};

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [confirmations, setConfirmations] = useState<ConfirmationMessage[]>([]);
  const [warningAlert, setWarningAlert] = useState<WarningAlertState | null>(null);
  const [errorAlert, setErrorAlert] = useState<ErrorAlertState | null>(null);
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const loadingSourcesRef = useRef(new Set<string>());

  const syncGlobalLoading = useCallback(() => {
    setIsGlobalLoading(loadingSourcesRef.current.size > 0);
  }, []);

  const setLoading = useCallback((active: boolean, source = 'default') => {
    if (active) {
      loadingSourcesRef.current.add(source);
    } else {
      loadingSourcesRef.current.delete(source);
    }
    syncGlobalLoading();
  }, [syncGlobalLoading]);

  const showError = useCallback((summary: string, details?: AppErrorDetails) => {
    setErrorAlert({
      summary,
      reportText: buildErrorReport(summary, details),
    });
  }, []);

  useEffect(() => {
    registerErrorReporter(showError);
    return () => registerErrorReporter(null);
  }, [showError]);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    if (type === 'success' || type === 'info') {
      return;
    }

    if (type === 'warning') {
      setWarningAlert({
        title: 'שים לב',
        message,
      });
      return;
    }

    showError(message);
  }, [showError]);

  const showSuccess = useCallback((_message: string, _duration?: number) => {
    // Success is silent; global loading overlay indicates work in progress.
  }, []);

  const showWarning = useCallback((message: string, _duration?: number) => {
    setWarningAlert({
      title: 'שים לב',
      message,
    });
  }, []);

  const showInfo = useCallback((_message: string, _duration?: number) => {
    // Informational messages (e.g. "no changes") are silent.
  }, []);

  const showConfirmation = useCallback((
    config: Omit<ConfirmationMessage, 'id' | 'onConfirm' | 'onSecondaryConfirm' | 'onCancel'>
  ): Promise<ConfirmationResult> => {
    return new Promise((resolve) => {
      const id = Math.random().toString(36).substr(2, 9);
      
      const confirmation: ConfirmationMessage = {
        ...config,
        id,
        onConfirm: () => {
          setConfirmations(prev => prev.filter(conf => conf.id !== id));
          resolve('confirm');
        },
        onSecondaryConfirm: config.secondaryConfirmText
          ? () => {
              setConfirmations(prev => prev.filter(conf => conf.id !== id));
              resolve('secondary');
            }
          : undefined,
        onCancel: () => {
          setConfirmations(prev => prev.filter(conf => conf.id !== id));
          resolve(false);
        },
      };

      setConfirmations(prev => [...prev, confirmation]);
    });
  }, []);

  const handleErrorClose = useCallback(() => {
    setErrorAlert(null);
  }, []);

  const contextValue: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    setLoading,
    showConfirmation,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <GlobalErrorHandlers />
      {isGlobalLoading && <GlobalLoadingOverlay />}
      {errorAlert && (
        <ErrorReportDialog
          reportText={errorAlert.reportText}
          onClose={handleErrorClose}
        />
      )}
      {warningAlert && (
        <AlertDialog
          title={warningAlert.title}
          message={warningAlert.message}
          type="warning"
          onClose={() => setWarningAlert(null)}
        />
      )}
      {confirmations.map(confirmation => (
        <ConfirmationDialog
          key={confirmation.id}
          id={confirmation.id}
          title={confirmation.title}
          message={confirmation.message}
          confirmText={confirmation.confirmText}
          secondaryConfirmText={confirmation.secondaryConfirmText}
          cancelText={confirmation.cancelText}
          type={confirmation.type}
          onConfirm={confirmation.onConfirm}
          onSecondaryConfirm={confirmation.onSecondaryConfirm}
          onCancel={confirmation.onCancel}
        />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
